import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import apiService from "../Common/Configurations/APIService";
import config from "../Common/Configurations/APIConfig";
import currencyFlags from "../Components/Data/CurrencyFlags.json"; // Import country flags JSON
import { HomePageSlider } from "./HomePageSlider";

export default function TaxInformation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [btnSubmit, setBtnSubmit] = useState(false);
  const [countries, setCountries] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [values, setValues] = useState({
    country: "IN",
    currency: "INR", // Added currency field
    gstNo: "",
    gstFile: null,
    industryId: "",
  });

  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [errors, setErrors] = useState({});

  // Get companyId and userEmail from location state
  const companyId = location.state?.companyId;
  const userEmail = location.state?.userEmail;

  useEffect(() => {
    // Redirect if no companyId (direct access)
    if (!companyId) {
      navigate("/register");
      return;
    }

    // Process countries data from CountryFlags.json
    if (currencyFlags && Array.isArray(currencyFlags)) {
      const sortedCountries = [...currencyFlags].sort((a, b) =>
        a.country.localeCompare(b.country)
      );

      setCountries(sortedCountries);

      // Set default country (India)
      const india = sortedCountries.find(
        (country) => country.countryCode === "IN"
      );
      if (india) {
        setValues((prev) => ({
          ...prev,
          country: "IN",
          currency: india.code || "INR",
        }));
      }
    }

    // Fetch industries like in registration form
    GetIndustries();
  }, [companyId, navigate]);

  // GetIndustries function from registration form
  const GetIndustries = async () => {
    try {
      const result = await apiService.getAll(config.GETALLINDUSTRIES);
      setIndustries(result);
    } catch (error) {
      console.error("Failed to fetch industries:", error);
    }
  };

  const handleTaxChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "gstFile") {
      setValues({ ...values, gstFile: files[0] });
      // Clear file error when user selects a new file
      if (errors.gstFile) {
        setErrors({ ...errors, gstFile: "" });
      }
    } else if (name === "country") {
      // When country changes, update both country and currency
      const selectedCountry = countries.find(
        (country) => country.countryCode === value
      );
      setValues({
        ...values,
        country: value,
        currency: selectedCountry?.code || "",
      });
    } else {
      setValues({ ...values, [name]: value });
    }

    // Clear GST number error when user starts typing
    if (name === "gstNo" && apiError) {
      setApiError("");
    }

    if (apiError) {
      setApiError("");
    }
  };

  const getCurrentCountryName = () => {
    const country = countries.find((c) => c.countryCode === values.country);
    return country ? country.country : "";
  };

  const getTaxFieldLabel = () => {
    const countryName = getCurrentCountryName().toLowerCase();
    if (countryName.includes("india")) return "GST Number*";
    return "Tax Number*";
  };

  const getFileUploadLabel = () => {
    const countryName = getCurrentCountryName().toLowerCase();
    if (countryName.includes("india")) return "GST Certificate";
    return "Tax Document";
  };

  const getPlaceholder = () => {
    const countryName = getCurrentCountryName().toLowerCase();
    if (countryName.includes("india")) return "Enter GST Number";
    return "Enter Tax Number";
  };

  const validate = () => {
    let temp = {};
    temp.industryId = values.industryId !== "";
    temp.gstNo = values.gstNo !== "";
    temp.gstFile = values.gstFile !== null && values.gstFile !== "";

    // File validation
    if (values.gstFile) {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/pdf",
      ];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(values.gstFile.type)) {
        temp.gstFile =
          "Invalid file type. Please upload JPG, PNG, or PDF files only.";
      } else if (values.gstFile.size > maxSize) {
        temp.gstFile = "File size too large. Maximum size is 5MB.";
      } else {
        temp.gstFile = true;
      }
    }

    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  const handleTaxSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setBtnSubmit(true);
      setLoading(true);
      setApiError("");

      try {
        const formData = new FormData();
        formData.append("companyId", companyId);
        formData.append("country", values.country);
        formData.append("currency", values.currency); // Added currency
        formData.append("gstNo", values.gstNo);
        formData.append("industryId", values.industryId);
        formData.append("domainUrl", window.location.origin);

        if (values.gstFile) {
          formData.append("gstFile", values.gstFile);
        }
        const taxResponse = await apiService.create(
          config.SAASACTIVATE,
          formData
        );

        console.log("Tax response:", taxResponse);

        if (taxResponse && taxResponse.success) {
          // Navigate to success/verification screen
          navigate("/registration-pending", {
            state: { userEmail },
          });
        }
      } catch (error) {
        // Handle GST number already exists error
        if (
          error.response &&
          error.response.data &&
          error.response.data.message === "GST number already exists."
        ) {
          setApiError(
            "This GST number is already registered. Please use a different GST number."
          );
        } else {
          setApiError(
            "An error occurred while submitting the form. Please try again."
          );
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBack = () => {
    navigate("/register");
  };

  const applyErrorClass = (field) =>
    field in errors && errors[field] === false
      ? " form-control-danger"
      : field in errors && typeof errors[field] === "string"
      ? " form-control-danger"
      : "";

  return (
    <div className="auth-page-wrapper py-5 d-flex justify-content-center align-items-center min-vh-100">
      <div className="bg-overlay" style={{ backgroundColor: "#F2F44E" }}></div>
      <div className="auth-page-content overflow-hidden mt-lg-4">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="card overflow-hidden">
                <div className="row g-0">
                  {/* Left Column - Form */}
                  <div className="col-lg-6">
                    <div className="card overflow-hidden rounded-4 loginbox shadow">
                      <div className="p-lg-5 p-4">
                        <h3 className="text-center">TAX INFORMATION</h3>
                        <p className="text-center text-muted mb-4">
                          Complete your registration by providing tax and
                          industry information
                        </p>

                        <form
                          onSubmit={handleTaxSubmit}
                          autoComplete="off"
                          noValidate
                        >
                          {/* Industry Selection */}
                          <div className="mb-3">
                            <label className="form-label">Industry*</label>
                            <select
                              className={
                                "form-select" + applyErrorClass("industryId")
                              }
                              name="industryId"
                              value={values.industryId}
                              onChange={handleTaxChange}
                            >
                              <option value="">Select Industry</option>
                              {industries.map((industry) => (
                                <option
                                  key={industry.industryId}
                                  value={industry.industryId}
                                >
                                  {industry.industryName}
                                </option>
                              ))}
                            </select>
                            {errors.industryId === false && (
                              <div className="validationerror">
                                Please select industry
                              </div>
                            )}
                          </div>

                          {/* Country Selection Dropdown */}
                          <div className="mb-3">
                            <label className="form-label">Country*</label>
                            <select
                              className={
                                "form-select" + applyErrorClass("country")
                              }
                              name="country"
                              value={values.country}
                              onChange={handleTaxChange}
                              required
                            >
                              <option value="">Select Country</option>
                              {countries.map((country) => (
                                <option
                                  key={country.countryCode}
                                  value={country.countryCode}
                                >
                                  {country.flag && (
                                    <img
                                      src={country.flag}
                                      alt={country.country}
                                      style={{
                                        width: "20px",
                                        height: "15px",
                                        marginRight: "8px",
                                        verticalAlign: "middle",
                                      }}
                                    />
                                  )}
                                  {country.country} - {country.countryCode}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Currency Field (hidden but sent to backend) */}
                          <input
                            type="hidden"
                            name="currency"
                            value={values.currency}
                          />

                          <div className="mb-3">
                            <label className="form-label">
                              {getTaxFieldLabel()}
                            </label>
                            <input
                              type="text"
                              className={
                                "form-control" + applyErrorClass("gstNo")
                              }
                              name="gstNo"
                              value={values.gstNo}
                              onChange={handleTaxChange}
                              placeholder={getPlaceholder()}
                              maxLength={25}
                              required
                            />
                            {errors.gstNo === false && (
                              <div className="validationerror">
                                Please enter gst number
                              </div>
                            )}
                          </div>

                          <div className="mb-3">
                            <label className="form-label">
                              Upload {getFileUploadLabel()}
                            </label>
                            <input
                              type="file"
                              className={
                                "form-control" + applyErrorClass("gstFile")
                              }
                              name="gstFile"
                              onChange={handleTaxChange}
                              accept=".jpg,.jpeg,.png,.pdf"
                            />
                            <small className="text-muted">
                              Supported: JPG, PNG, PDF (Max: 5MB)
                            </small>
                            {errors.gstFile === false && (
                              <div className="validationerror">
                                Please upload gst file
                              </div>
                            )}
                            {typeof errors.gstFile === "string" && (
                              <div className="validationerror">
                                {errors.gstFile}
                              </div>
                            )}
                          </div>

                          {apiError && (
                            <div className="alert alert-danger">{apiError}</div>
                          )}

                          <div className="mt-4 d-flex justify-content-end">
                            {!loading ? (
                              <button
                                type="submit"
                                className="btn btn-success w-100 me-2"
                              >
                                Submit
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-primary w-100 me-2"
                                disabled
                              >
                                Submitting...
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-danger me-2 w-100"
                              onClick={handleBack}
                            >
                              Back
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                  <HomePageSlider />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
